/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Container,
  Form,
  Row,
  Tab,
  Tabs,
  Table,
  Badge,
  Spinner,
} from 'react-bootstrap';
import { useAppSelector } from '../hooks/hooks';
import ChangePasswordModal from '../components/settings/ChangePasswordModal';
import ChangeEmailModal from '../components/settings/ChangeEmailModal';
import { supabase } from '../lib/supabase';
import { useRecordPageView } from '../hooks/useRecordPageView';
import { SubscriptionUpgradeModal } from '../components/settings/SubscriptionUpgradeModal';
import { useSearchParams } from 'react-router-dom';
import { captureSupabaseError } from '../lib/sentry';
import { tracking } from '../util/tracking';

// Define the interface based on your SQL View
export interface ViewOrganisation {
  profile_id: string;
  role: string;
  status: string;
  organisation_id: string;
  organisation_name: string;
  plan_name: string | null;
  tier: string | null;
  features: any;
  billing_customer_id: string | null;
}

const MySettings: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [orgsLoading, setOrgsLoading] = useState(true); // Separate loading for orgs
  const [error, setError] = useState<string | null>(null);
  const [organisations, setOrganisations] = useState<ViewOrganisation[]>([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPlanUpgradeOrg, setSelectedPlanUpgradeOrg] =
    useState<ViewOrganisation | null>(null);

  const [changePassword, setChangePassword] = useState(false);
  const [changeEmail, setChangeEmail] = useState(false);
  const [firstName, setFirstName] = useState(user?.profile?.first_name || '');
  const [lastName, setLastName] = useState(user?.profile?.last_name || '');

  const subscriptionUpdated = searchParams.get('subscription_updated');
  const flow = searchParams.get('flow'); // e.g., 'upgrade' or 'signup'

  useRecordPageView();

  // Fetch Organisations from the view
  useEffect(() => {
    const fetchOrganisations = async () => {
      if (!user?.id) return;

      try {
        setOrgsLoading(true);
        const { data, error: orgError } = await supabase
          .from('v_my_orgs')
          .select('*')
          .eq('profile_id', user.id);

        if (orgError) throw orgError;
        setOrganisations(data || []);
      } catch (err: any) {
        captureSupabaseError(err, { op: 'organisations.fetch' });
        console.error('Error fetching organisations:', err.message);
      } finally {
        setOrgsLoading(false);
      }
    };

    fetchOrganisations();
  }, [user?.id]);

  const onChange = (field: string, value: string) => {
    switch (field) {
      case 'first_name':
        setFirstName(value);
        break;
      case 'last_name':
        setLastName(value);
        break;
      default:
        break;
    }
  };

  const onSave = async () => {
    tracking.log('UI', 'settings_save', { page: 'my_settings' });
    if (!firstName || !lastName) {
      setError('First and last name are required');
      return;
    }
    setError(null);
    setLoading(true);

    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          first_name: firstName,
          last_name: lastName,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user?.id);

      if (updateError) throw updateError;
    } catch (err: any) {
      captureSupabaseError(err, { op: 'profile.update' });
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const getPlanDisplay = (org: ViewOrganisation) => {
    if (!org.plan_name || org.plan_name.toLowerCase() === 'free')
      return (
        <>
          Free{' '}
          <Button
            variant="link"
            className="p-0 ms-2"
            onClick={() => {
              tracking.log('UI', 'plan_upgrade_open', {
                organisationId: org.organisation_id,
                planName: org.plan_name ?? 'free',
              });
              setShowUpgradeModal(true);
              setSelectedPlanUpgradeOrg(org);
            }}
          >
            Upgrade
          </Button>
        </>
      );

    return (
      <>
        {org.plan_name}{' '}
        <Button
          variant="link"
          className="p-0 ms-2"
          onClick={() => {
            tracking.log('UI', 'plan_upgrade_open', {
              organisationId: org.organisation_id,
              planName: org.plan_name ?? 'unknown',
            });
            setShowUpgradeModal(true);
            setSelectedPlanUpgradeOrg(org);
          }}
        >
          Change plan
        </Button>
      </>
    );
  };

  return (
    <Container fluid>
      <h3 className="mt-4">My Settings</h3>
      <p className="text-muted">
        Manage your account settings and preferences here.
      </p>

      {subscriptionUpdated === 'true' && (
        <div className="alert alert-success">
          {flow === 'update'
            ? 'Subscription updated successfully!'
            : 'Subscription activated successfully!'}
        </div>
      )}

      <Tabs defaultActiveKey="account" id="settings-tabs" className="mb-4">
        {/* Account Tab */}
        <Tab eventKey="account" title="Account">
          <Row>
            <Col md={6} lg={4}>
              <Form>
                {error && <div className="alert alert-danger">{error}</div>}
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={firstName}
                    onChange={(e) => onChange('first_name', e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={lastName}
                    onChange={(e) => onChange('last_name', e.target.value)}
                  />
                </Form.Group>
                <Button variant="primary" disabled={loading} onClick={onSave}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </Form>
            </Col>
            <Col md={6} lg={4}>
              <Form.Group className="mb-3">
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  readOnly
                  disabled
                  value={user?.email || ''}
                />
                <Button
                  variant="link"
                  className="p-0 mt-2"
                  onClick={() => setChangeEmail(true)}
                >
                  Change email
                </Button>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="••••••••"
                  readOnly
                  disabled
                />
                <Button
                  variant="link"
                  className="p-0 mt-2"
                  onClick={() => setChangePassword(true)}
                >
                  Change Password
                </Button>
              </Form.Group>
            </Col>
          </Row>
        </Tab>

        {/* Organisation Tab */}
        <Tab eventKey="organisation" title="Organisation">
          {organisations.length > 1 && (
            <p className="text-muted">
              To select organisation use the dropdown menu in the main menu.
            </p>
          )}

          {orgsLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p>Loading organisations...</p>
            </div>
          ) : organisations.length > 0 ? (
            <Table responsive hover className="mt-3">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Plan</th>
                </tr>
              </thead>
              <tbody>
                {organisations.map((org) => (
                  <tr key={org.organisation_id}>
                    <td>
                      <strong>{org.organisation_name}</strong>
                    </td>
                    <td>
                      <Badge bg="secondary">{org.role}</Badge>
                    </td>
                    <td>
                      <Badge
                        bg={org.status === 'active' ? 'success' : 'warning'}
                      >
                        {org.status}
                      </Badge>
                    </td>
                    <td>{getPlanDisplay(org)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p className="text-muted">
              You are not a member of any organisations.
            </p>
          )}
        </Tab>
      </Tabs>

      <ChangePasswordModal
        show={changePassword}
        onHide={() => setChangePassword(false)}
      />
      <ChangeEmailModal
        show={changeEmail}
        onHide={() => setChangeEmail(false)}
      />
      <SubscriptionUpgradeModal
        show={showUpgradeModal}
        onHide={() => setShowUpgradeModal(false)}
        organisation={selectedPlanUpgradeOrg}
      />
    </Container>
  );
};

export default MySettings;
